import React from "react";
import { Box, Typography } from "@mui/material";

interface ProgressBarProps {
  stages: string[];
  currentStage: string | null;
}

interface StepShapeProps {
  index: number;
  isActive: boolean;
  isCompleted: boolean;
  totalSteps: number;
}

const StepShape: React.FC<StepShapeProps> = ({
  index,
  isActive,
  isCompleted,
  totalSteps,
}) => {
  const stepWidth = 405 / totalSteps; // Calculate width based on total steps
  let textColor = "#414141";

  if (isActive || isCompleted) {
    textColor = "#FFFFFF";
  }

  const getStepSvg = (stepType: "first" | "middle" | "last") => {
    const fill = (() => {
      if (isActive) return "#4E1BD9";
      if (isCompleted) return "#AAA8F7";
      return "#FEFEFE";
    })();
    const stroke = "#E7E7E7";

    switch (stepType) {
      case "first":
        return (
          <svg
            width={stepWidth}
            height="30"
            viewBox={`0 0 ${stepWidth} 30`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d={`M0 5C0 2.23858 2.23858 0 5 0H${
                stepWidth - 12
              }L${stepWidth} 15L${
                stepWidth - 12
              } 30H5C2.23858 30 0 27.7614 0 25V5Z`}
              fill={fill}
              stroke={stroke}
            />
          </svg>
        );
      case "middle":
        return (
          <svg
            width={stepWidth}
            height="30"
            viewBox={`0 0 ${stepWidth} 30`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d={`M0 0L12 15L0 30H${stepWidth - 12}L${stepWidth} 15L${
                stepWidth - 12
              } 0H0Z`}
              fill={fill}
              stroke={stroke}
            />
          </svg>
        );
      case "last":
        return (
          <svg
            width={stepWidth}
            height="30"
            viewBox={`0 0 ${stepWidth} 30`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d={`M0 0L12 15L0 30H${stepWidth - 5}C${
                stepWidth - 2.23858
              } 30 ${stepWidth} 27.7614 ${stepWidth} 25V5C${stepWidth} 2.23858 ${
                stepWidth - 2.23858
              } 0 ${stepWidth - 5} 0H0Z`}
              fill={fill}
              stroke={stroke}
            />
          </svg>
        );
    }
  };

  let stepType: "first" | "middle" | "last";

  if (index === 0) {
    stepType = "first";
  } else if (index === totalSteps - 1) {
    stepType = "last";
  } else {
    stepType = "middle";
  }

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        width: stepWidth,
        marginLeft: index === 0 ? 0 : "-12px", // Overlap steps
        zIndex: totalSteps - index, // Ensure proper layering
      }}
    >
      <Box sx={{ position: "relative", width: stepWidth, height: 30 }}>
        {getStepSvg(stepType)}
        <Typography
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: textColor,
            fontSize: "10px",
            fontWeight: 750,
            whiteSpace: "nowrap",
          }}
        >
          Step {index + 1}
        </Typography>
      </Box>
    </Box>
  );
};

const ProgressBar: React.FC<ProgressBarProps> = ({ stages, currentStage }) => {
  const currentStepIndex = currentStage ? stages.indexOf(currentStage) : -1;

  if (stages.length === 0 || currentStepIndex === -1) {
    return null;
  }

  return (
    <Box
      sx={{
        width: "380px",
        bgcolor: "#F2F6FF",
        padding: "12px 16px",
        display: "flex",
        flexDirection: "column",
        height: "auto",
      }}
    >
      <Box
        sx={{
          display: "flex",
          width: "380px",
          borderRadius: "5px",
          alignItems: "center",
          justifyContent: "flex-start",
          "&::-webkit-scrollbar": { display: "none" },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          overflow: "hidden", // Ensure no horizontal scrollbar
        }}
      >
        {stages.map((stage, index) => (
          <StepShape
            key={stage}
            index={index}
            isActive={index === currentStepIndex}
            isCompleted={index < currentStepIndex}
            totalSteps={stages.length}
          />
        ))}
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mt: 1,
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            color: "#2A2A2A",
            fontSize: "14px",
            fontWeight: 700,
            ml: 0.5,
            maxWidth: "60%",
            overflow: "hidden",
            whiteSpace: "nowrap",
          }}
        >
          {currentStage}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            maxWidth: "60%",
          }}
        >
          <Typography
            sx={{ color: "#343942", fontSize: "10px", mr: 0.5, mt: 0.5 }}
          >
            {/* Estimated Time: 30 Sec */}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ProgressBar;
